import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { Bell, Users, Lightbulb, MessageSquare, UserCheck, UserX, Check, Send, MessageCircle, X, HeartHandshake, Heart } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import ThankBackSheet from "./ThankBackSheet";
import { LinkedAvatar } from "./LinkedProfile";

const API = '/api';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [processingConnection, setProcessingConnection] = useState(null);
  const [thankBackContext, setThankBackContext] = useState(null); // {connection_id, target_name, target_picture, card_id}
  const dropdownRef = useRef(null);
  const replyInputRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API}/notifications`, { credentials: 'include' });
      if (response.ok) {
        setNotifications(await response.json());
      }
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setReplyingTo(null);
        setReplyText("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (replyingTo && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingTo]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = async (notifId) => {
    try {
      await fetch(`${API}/notifications/${notifId}/read`, {
        method: 'POST',
        credentials: 'include'
      });
      setNotifications(prev => prev.map(n =>
        n.notification_id === notifId ? { ...n, read: true } : n
      ));
    } catch (error) {
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: 'POST',
        credentials: 'include'
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
    }
  };

  const getRoute = (notif) => {
    switch (notif.type) {
      case 'insight_request':
        return `/insights/give/${notif.related_id}`;
      case 'insight_response':
        return `/insights/view/${notif.related_id}`;
      case 'insight_message':
        return `/insights/give/${notif.related_id}`;
      case 'connection_request':
        return '/notifications';
      case 'connection_accepted':
      case 'connection_declined':
        return '/network';
      case 'gratitude_ask':
        return `/tools/gratitude?respond=${notif.related_id}`;
      case 'gratitude_received':
      case 'gratitude_response':
        return '/tools/gratitude?tab=received';
      case 'gratitude_connection_request':
      case 'gratitude_accepted':
        return '/dashboard';
      default:
        return '/dashboard';
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'insight_request': return Lightbulb;
      case 'insight_response': return MessageSquare;
      case 'insight_message': return MessageCircle;
      case 'connection_request': return Users;
      case 'connection_accepted': return UserCheck;
      case 'connection_declined': return UserX;
      case 'gratitude_ask':
      case 'gratitude_received':
      case 'gratitude_response':
      case 'gratitude_connection_request':
      case 'gratitude_accepted':
        return HeartHandshake;
      default: return Bell;
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'insight_request': return 'text-violet-600 bg-violet-50';
      case 'insight_response': return 'text-green-600 bg-green-50';
      case 'insight_message': return 'text-blue-600 bg-blue-50';
      case 'connection_request': return 'text-purple-600 bg-purple-50';
      case 'connection_accepted': return 'text-emerald-600 bg-emerald-50';
      case 'connection_declined': return 'text-slate-500 bg-slate-50';
      case 'gratitude_ask': return 'text-amber-600 bg-amber-50';
      case 'gratitude_received':
      case 'gratitude_response':
      case 'gratitude_accepted':
        return 'text-rose-600 bg-rose-50';
      case 'gratitude_connection_request': return 'text-fuchsia-600 bg-fuchsia-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.read) {
      await markAsRead(notif.notification_id);
    }
    setOpen(false);
    setReplyingTo(null);
    setReplyText("");
    navigate(getRoute(notif));
  };

  const handleReplyClick = (e, notif) => {
    e.stopPropagation();
    if (replyingTo === notif.notification_id) {
      setReplyingTo(null);
      setReplyText("");
    } else {
      setReplyingTo(notif.notification_id);
      setReplyText("");
    }
  };

  const sendReply = async (e, notif) => {
    e.preventDefault();
    e.stopPropagation();
    if (!replyText.trim() || sendingReply) return;

    setSendingReply(true);
    try {
      const res = await fetch(`${API}/insights/${notif.related_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: replyText.trim() })
      });
      if (res.ok) {
        toast.success('Reply sent!');
        setReplyingTo(null);
        setReplyText("");
        if (!notif.read) {
          markAsRead(notif.notification_id);
        }
      } else {
        toast.error('Failed to send reply');
      }
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString();
  };

  const acceptConnection = async (e, notif) => {
    e.stopPropagation();
    setProcessingConnection(notif.notification_id);
    try {
      const res = await fetch(`${API}/network/${notif.related_id}/accept`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setNotifications(prev => prev.map(n =>
          n.notification_id === notif.notification_id
            ? { ...n, read: true, type: 'connection_accepted', title: 'Connected!', message: notif.message.replace('wants to connect', 'is now connected') }
            : n
        ));
        const handle = notif.from_user_username ? ` (@${notif.from_user_username})` : '';
        toast.success(`Connection accepted!${handle}`);
      } else {
        toast.error('Failed to accept');
      }
    } catch {
      toast.error('Failed to accept');
    } finally {
      setProcessingConnection(null);
    }
  };

  const declineConnection = async (e, notif) => {
    e.stopPropagation();
    setProcessingConnection(notif.notification_id);
    try {
      const res = await fetch(`${API}/network/${notif.related_id}/decline`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setNotifications(prev => prev.map(n =>
          n.notification_id === notif.notification_id
            ? { ...n, read: true, type: 'connection_declined', title: 'Declined', message: notif.message.replace('wants to connect with you', 'request declined') }
            : n
        ));
        toast.success('Connection declined');
      } else {
        toast.error('Failed to decline');
      }
    } catch {
      toast.error('Failed to decline');
    } finally {
      setProcessingConnection(null);
    }
  };

  const renderNotification = (notif) => {
    const Icon = getIcon(notif.type);
    const isMessageType = notif.type === 'insight_message';
    const meta = notif.metadata || {};
    const isReplying = replyingTo === notif.notification_id;
    const senderPicture = notif.from_user_picture || meta.sender_picture;
    const senderName = notif.from_user_name || meta.sender_name;
    const showAvatar = senderPicture || senderName;

    return (
      <div
        key={notif.notification_id}
        className={`border-b border-slate-50 last:border-0 ${!notif.read ? 'bg-violet-50/40' : ''}`}
        data-testid={`notif-item-${notif.notification_id}`}
      >
        <button
          onClick={() => handleNotificationClick(notif)}
          className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors"
        >
          {/* Avatar when sender info is available, otherwise icon */}
          {showAvatar ? (
            <LinkedAvatar username={notif.from_user_username} className="flex-shrink-0">
              <Avatar className="h-8 w-8">
                <AvatarImage src={senderPicture} alt={senderName} />
                <AvatarFallback className={`text-xs ${getIconColor(notif.type)}`}>
                  {senderName?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
            </LinkedAvatar>
          ) : (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getIconColor(notif.type)}`}>
              <Icon className="w-4 h-4" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            {isMessageType ? (
              <>
                <div className="flex items-center gap-1.5">
                  <p className={`text-sm leading-snug ${!notif.read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                    {notif.title}
                  </p>
                  <span className="text-xs text-slate-400">{meta.subtitle}</span>
                </div>
                <p className="text-sm text-slate-800 mt-1 leading-relaxed line-clamp-3" data-testid={`notif-message-content-${notif.notification_id}`}>
                  {notif.message}
                </p>
              </>
            ) : (
              <>
                <p className={`text-sm leading-snug ${!notif.read ? 'font-medium text-slate-900' : 'text-slate-700'}`}>
                  {notif.title}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{notif.message}</p>
              </>
            )}

            <div className="flex items-center gap-3 mt-1.5">
              <p className="text-[10px] text-slate-400">{formatTime(notif.created_at)}</p>
              {notif.from_user_username && (
                <a
                  href={`/${notif.from_user_username}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[10px] text-slate-400 hover:text-violet-600"
                  data-testid={`notif-handle-${notif.notification_id}`}
                >
                  @{notif.from_user_username}
                </a>
              )}
              {isMessageType && (
                <button
                  onClick={(e) => handleReplyClick(e, notif)}
                  className={`text-[11px] font-medium flex items-center gap-1 transition-colors ${
                    isReplying ? 'text-violet-700' : 'text-violet-500 hover:text-violet-700'
                  }`}
                  data-testid={`notif-reply-toggle-${notif.notification_id}`}
                >
                  <MessageCircle className="w-3 h-3" />
                  {isReplying ? 'Cancel' : 'Reply'}
                </button>
              )}
            </div>

            {/* Accept/Decline for connection requests */}
            {notif.type === 'connection_request' && (
              <div className="flex items-center gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={(e) => acceptConnection(e, notif)}
                  disabled={processingConnection === notif.notification_id}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full h-7 text-xs px-3"
                  data-testid={`notif-accept-${notif.notification_id}`}
                >
                  <Check className="w-3 h-3 mr-1" />
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => declineConnection(e, notif)}
                  disabled={processingConnection === notif.notification_id}
                  className="border-red-200 text-red-600 hover:bg-red-50 rounded-full h-7 text-xs px-3"
                  data-testid={`notif-decline-${notif.notification_id}`}
                >
                  <X className="w-3 h-3 mr-1" />
                  Decline
                </Button>
              </div>
            )}

            {/* Inline "Thank back" CTA on gratitude_accepted notifications */}
            {notif.type === 'gratitude_accepted' && notif.metadata?.connection_id && (
              <div className="flex items-center gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setThankBackContext({
                      connection_id: notif.metadata.connection_id,
                      target_name: notif.metadata.target_name || notif.from_user_name,
                      target_picture: notif.from_user_picture,
                      target_username: notif.from_user_username,
                      card_id: notif.related_id,
                    });
                    setOpen(false);
                  }}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full h-7 text-xs px-3"
                  data-testid={`notif-thank-back-${notif.notification_id}`}
                >
                  <Heart className="w-3 h-3 mr-1 fill-white" />
                  Thank back
                </Button>
              </div>
            )}
          </div>

          {!notif.read && (
            <div className="w-2 h-2 bg-violet-500 rounded-full flex-shrink-0 mt-2" />
          )}
        </button>

        {/* Inline reply input */}
        {isReplying && (
          <form
            onSubmit={(e) => sendReply(e, notif)}
            className="px-4 pb-3 flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
            data-testid={`notif-reply-form-${notif.notification_id}`}
          >
            <input
              ref={replyInputRef}
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={`Reply to ${meta.sender_name || 'them'}...`}
              className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-200"
              data-testid={`notif-reply-input-${notif.notification_id}`}
            />
            <Button
              type="submit"
              size="sm"
              disabled={!replyText.trim() || sendingReply}
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full h-8 w-8 p-0 flex-shrink-0"
              data-testid={`notif-reply-send-${notif.notification_id}`}
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </form>
        )}
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => {
          const wasOpen = open;
          setOpen(!open);
          if (!wasOpen && unreadCount > 0) {
            markAllAsRead();
          }
        }}
        className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'system-ui, sans-serif' }}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1"
                data-testid="mark-all-read"
              >
                <Check className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.slice(0, 15).map(renderNotification)
            ) : (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No notifications yet</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-slate-100">
              <button
                onClick={() => { setOpen(false); navigate('/notifications'); }}
                className="w-full text-center py-2.5 text-sm text-violet-600 hover:text-violet-700 font-medium hover:bg-slate-50 transition-colors"
                data-testid="view-all-notifications"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mini thank-back composer triggered from gratitude_accepted notifications */}
      {thankBackContext && (
        <ThankBackSheet
          open={!!thankBackContext}
          onOpenChange={(v) => { if (!v) setThankBackContext(null); }}
          targetName={thankBackContext.target_name}
          targetPicture={thankBackContext.target_picture}
          targetUsername={thankBackContext.target_username}
          connectionId={thankBackContext.connection_id}
          originalCardId={thankBackContext.card_id}
          onSuccess={() => { setThankBackContext(null); fetchNotifications(); }}
        />
      )}
    </div>
  );
};

export default NotificationBell;
